import { useEffect, useState, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { useTypewriter } from "components/hooks/useTypewriter";

const messageSets = {
  default: [
    // Practical / believable ones
    "Connecting the dots between past and future plans...",
    "Analyzing past tasks...",
    "Checking calendars for hidden gems...",
    "Looking at upcoming events...",
    "Reviewing past calendar events...",
    "Identifying the tasks you almost forgot...",
    "Matching ideas to schedules...",
    "Finding patterns in to-dos...",
    "Cross-checking seasonal trends...",
    "Surfacing recurring priorities...",
    "Assessing recent communications...",

    // Fun / silly ones
    "Brewing a fresh pot of idea coffee...",
    "Peeking into your future through the task crystal ball...",
    "Reading your subconscious mind...",
  ],
  newUser: [
    "Gathering ideas that match your goals...",
    "Personalizing suggestions for your family...",
    "Checking what other families like yours are doing...",
    "Prioritizing things that are most important to you...",
    "Brewing a fresh pot of idea coffee...",
    "Stirring in fresh ideas...",
  ],
  agent: [
    "Starting up agent...",
    "Getting everything ready...",
    "Preparing to work my magic...",
    "Firing up all cylinders...",
    "Gathering ones and zeros...",
    "Getting my superpowers ready...",
  ],
};

interface AILoadingProps {
  icon?: ReactNode;
  noIcon?: boolean;
  textAnimated?: boolean;
  messages?: string;
  size?: "default" | "small";
}

export default ({
  icon,
  noIcon = false,
  textAnimated = false,
  messages = "default",
  size = "default",
}: AILoadingProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [shuffledMessages] = useState(() => {
    // Get the appropriate message set
    const messageArray =
      messageSets[messages as keyof typeof messageSets] || messageSets.default;

    // Create a shuffled copy of the loading messages
    const shuffled = [...messageArray];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const typewriterText = useTypewriter({
    texts: shuffledMessages,
    enabled: textAnimated,
    typingSpeed: 30,
    erasingSpeed: 15,
    pauseDuration: 2500,
  });

  useEffect(() => {
    if (!textAnimated) {
      const interval = setInterval(() => {
        setCurrentMessageIndex(
          (prevIndex) => (prevIndex + 1) % shuffledMessages.length,
        );
      }, 2500);

      return () => clearInterval(interval);
    }
  }, [textAnimated, shuffledMessages]);

  const displayText = textAnimated
    ? typewriterText
    : shuffledMessages[currentMessageIndex];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: size === "small" ? "row" : "column",
        alignItems: "center",
        gap: "8px",
        minHeight: size === "small" ? "32px" : "92px",
        // When the text resets, the height shouldn't change or it causes a flash
        // "small" (flex row) - max(text height, icon height)
        // "default" (flex column) icon + gap + text
      }}
    >
      {!noIcon &&
        (icon || (
          <img
            src="/assets/images/rocket.gif"
            style={{
              width: size === "small" ? "32px" : "64px",
              height: size === "small" ? "32px" : "64px",
            }}
          />
        ))}
      <Typography
        variant="bodyHeavy"
        color="text.secondary"
        sx={{
          textAlign: "center",
          animation: "fadeIn 0.5s ease-in-out",
          minHeight: "20px",
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};
